import type { Database } from '../../../../lib/database.types';
import { supabase } from '../../../../lib/supabase';

// Helper function to safely execute Supabase queries with retry logic
type ClientTaskUpdate = Database['public']['Tables']['client_tasks']['Update'];

export const safeSupabaseQuery = async <T,>(queryFn: () => PromiseLike<T>, retries = 3, delay = 1000): Promise<T> => {
  let attempt = 0;
  while (attempt < retries) {
    try {
      return await queryFn();
    } catch (error) {
      console.error(`Supabase query attempt ${attempt + 1} failed:`, error);
      attempt++;
      if (attempt >= retries) {
        throw error;
      }
      await new Promise(resolve => setTimeout(resolve, delay));
      // Increase delay for next retry (exponential backoff)
      delay *= 2;
    }
  }
  // Unreachable: the loop always returns or throws.
  throw new Error('Supabase query failed after retries');
};

// Function to check authentication and admin status
export const checkAdminStatus = async () => {
  try {
    const { data: sessionData } = await safeSupabaseQuery(() => supabase.auth.getSession());
    if (!sessionData.session) {
      return { isAuthenticated: false, isAdmin: false, userId: null };
    }

    // Check if user is admin
    const { data: userData, error: userError } = await safeSupabaseQuery(() =>
      supabase
        .from('users')
        .select('role')
        .eq('id', sessionData.session.user.id)
        .single()
    );

    if (userError) {
      console.error('Error checking user role:', userError);
      return { isAuthenticated: true, isAdmin: false, userId: sessionData.session.user.id };
    }

    return {
      isAuthenticated: true,
      isAdmin: userData?.role === 'admin' || userData?.role === 'agency_owner',
      userId: sessionData.session.user.id
    };
  } catch (error) {
    console.error('Error in checkAdminStatus:', error);
    return { isAuthenticated: false, isAdmin: false, userId: null };
  }
};

// Function to fetch users for assignment dropdown
export const fetchUsers = async () => {
  try {
    const { data: { session } } = await safeSupabaseQuery(() =>
      supabase.auth.getSession()
    );
    
    if (!session?.user) {
      return { data: [], error: 'No authenticated session' };
    }

    const { data, error } = await safeSupabaseQuery(() =>
      supabase
        .from('users')
        .select('id, name, email')
        .order('name')
    );

    if (error) {
      console.error('Error fetching users:', error);
      return { data: [], error: error.message };
    }

    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error in fetchUsers:', error);
    return { data: [], error: 'Failed to fetch users' };
  }
};

// Function to fetch task templates (deprecated - table removed)
export const fetchTemplates = async () => {
  // task_templates table has been removed
  return { data: [], error: null };
};

// Function to fetch client tasks
export const fetchClientTasks = async (
  clientId: string,
  options: { archived?: boolean; includeArchived?: boolean } = {}
) => {
  try {
    if (!clientId) {
      return { data: [], error: 'Client ID is required' };
    }

    const { data: { session } } = await safeSupabaseQuery(() =>
      supabase.auth.getSession()
    );
    
    if (!session) {
      return { data: [], error: 'No authenticated session' };
    }

    const { archived, includeArchived } = options;

    let query = supabase
      .from('client_tasks')
      .select(`
        id,
        title,
        status,
        client_tasks_priority,
        client_backlog_priority,
        due_date,
        assigned_by,
        assigned_to,
        assigned_to_name,
        description,
        video_url,
        metadata,
        created_at,
        updated_at,
        archived,
        archived_at
      `)
      .eq('client_id', clientId)
      .order('created_at', { ascending: false });

    if (typeof archived === 'boolean') {
      query = query.eq('archived', archived);
    } else if (!includeArchived) {
      query = query.eq('archived', false);
    }

    const { data, error } = await safeSupabaseQuery(() => query);

    if (error) {
      console.error('Error fetching client tasks:', error);
      return { data: [], error: error.message };
    }

    const tasksWithWebsite = await Promise.all(
      ((data || []) as Array<{ metadata?: { website_id?: string } | null } & Record<string, unknown>>).map(async (task) => {
        const websiteId = task.metadata?.website_id;
        if (websiteId) {
          try {
            const { data: websiteData, error: websiteError } = await safeSupabaseQuery(() =>
              supabase
                .from('websites')
                .select('id, url, title')
                .eq('id', websiteId)
                .single()
            );

            if (!websiteError && websiteData) {
              return {
                ...task,
                website_id: websiteData.id,
                website_title: websiteData.title || websiteData.url
              };
            }
          } catch (err) {
            console.error('Error fetching website for task:', err);
          }
        }
        return task;
      })
    );

    return { data: tasksWithWebsite, error: null };
  } catch (error) {
    console.error('Error in fetchClientTasks:', error);
    return { data: [], error: 'Failed to fetch client tasks' };
  }
};

export interface UpdateTaskInput {
  title?: string;
  status?: string;
  dueDate?: string | null;
  assignedTo?: string | null;
}

// Function to update a task
export const updateTask = async (taskId: string, updatedData: UpdateTaskInput) => {
  try {
    // Log the update data for debugging
    
    // If assigned_to is being updated, use the update_task_assignment function
    if ('assignedTo' in updatedData) {
      const { data, error } = await supabase.rpc('update_task_assignment', {
        p_task_id: taskId,
        // null unassigns; the generated arg type is non-null because the SQL arg has no default
        p_assigned_to: (updatedData.assignedTo ?? null) as unknown as string,
      });
      
      if (error) {
        console.error('Error updating task assignment:', error);
        return { success: false, error: error.message };
      }
      
      
      // Remove assignedTo from updatedData since it's already been handled
      const otherUpdates = { ...updatedData };
      delete otherUpdates.assignedTo;
      updatedData = otherUpdates;
      
      // If there are no other updates, return success
      if (Object.keys(updatedData).length === 0) {
        return { success: true, data };
      }
    }
    
    // Prepare the update data
    const updateData: ClientTaskUpdate = {};
    
    if ('title' in updatedData) updateData.title = updatedData.title;
    if ('status' in updatedData) updateData.status = updatedData.status;
    if ('dueDate' in updatedData) updateData.due_date = updatedData.dueDate;
    
    // Only proceed if there are fields to update
    if (Object.keys(updateData).length > 0) {
      
      const { data, error } = await supabase
        .from('client_tasks')
        .update(updateData)
        .eq('id', taskId)
      
      if (error) {
        console.error('Error updating task:', error);
        return { success: false, error: error.message };
      }
      
      return { success: true, data };
    }
    
    return { success: true };
  } catch (error) {
    console.error('Exception updating task:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error occurred' 
    };
  }
};